import { LoadError, useGlobalLoading } from '@frontends/feature-tv-parts';
import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { EmptyAccounts } from '../components/emptyAccounts';
import { $axios } from '../hooks/useAxiosInterceptors';
import { useEntityConfig } from '../hooks/useEntityConfig';
import { TClientAccount } from '../types';

export interface IAccount {
  clientAccounts: TClientAccount[];
  clientId: number | null;
  maskLogin: (login: number | null) => string;
  maskLoginDesc: (desc: string) => string;
  selectAccount: (
    accountId: number,
    isUsePersistenceService?: boolean,
    clientAccountsList?: TClientAccount[],
    clientIdValue?: number | null,
  ) => Promise<void>;
  selectedClientAccount: TClientAccount | null;
  selectedClientAccountId: number | null;
}

export const Account = createContext<IAccount>({
  selectedClientAccountId: null,
  selectedClientAccount: null,
  clientAccounts: [],
  clientId: null,
  selectAccount: () => Promise.resolve(),
  maskLogin: (login) => (login === null ? '' : login.toString()),
  maskLoginDesc: (desc) => desc,
});

interface RawClientAccount {
  accountNumber: number;
  caption?: string;
  description?: string;
}

export const AccountProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const config = useEntityConfig();

  const [clientAccounts, setClientAccounts] = useState<TClientAccount[]>([]);
  const [selectedClientAccount, setSelectedClientAccount] = useState<TClientAccount | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    let isMounted = true;

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000];

    const initClientProfile = async (): Promise<void> => {
      if (!isMounted) return;

      setIsLoading(true);
      setHasLoadError(false);

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (signal.aborted || !isMounted) return;

        try {
          let accountsLocalScope: TClientAccount[] = [];
          let clientIdLocalScope: number | null = null;

          const { data } = await $axios.get(`${config.VITE_URL_ID_BACKEND}/api/v1/clients/current`, {
            signal,
          });

          if (signal.aborted) return;

          const {
            client_details: { accounts, id },
          } = data;

          const mappedAccounts = (accounts as RawClientAccount[]).map((account) => ({
            id: account.accountNumber,
            description: account.description || account.caption || '',
          }));

          accountsLocalScope = mappedAccounts;
          clientIdLocalScope = id;

          if (!isMounted) return;

          setClientAccounts(accountsLocalScope);
          setClientId(clientIdLocalScope);

          if (accountsLocalScope.length && clientIdLocalScope) {
            const accountIdListLocalScope = accountsLocalScope.map((account) => account.id);

            try {
              const { data: lastSelectedAccount } = await $axios.post(
                `${config.VITE_URL_PERSISTENCE_SERVICE}/api/v1/last-selected-account/get`,
                {
                  client_id_external: clientIdLocalScope,
                  account_id_external_list: accountIdListLocalScope,
                },
                { signal },
              );

              if (signal.aborted) return;

              if (lastSelectedAccount) {
                await selectAccount(lastSelectedAccount, false, accountsLocalScope, clientIdLocalScope);
              } else {
                const [firstAccountId] = accountIdListLocalScope;
                await selectAccount(firstAccountId, false, accountsLocalScope, clientIdLocalScope);
              }
            } catch {
              if (signal.aborted) return;

              const [firstAccountId] = accountIdListLocalScope;
              await selectAccount(firstAccountId, false, accountsLocalScope, clientIdLocalScope);
            }
          }

          break;
        } catch {
          if (signal.aborted) return;

          if (attempt < MAX_RETRIES) {
            console.warn(`Error loading client profile, retrying (${attempt + 1}/${MAX_RETRIES})...`);
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
          } else {
            console.error('Error loading client profile after all retries');

            if (isMounted) setHasLoadError(true);
          }
        }
      }

      if (isMounted) {
        setIsLoading(false);
      }
    };

    void initClientProfile();

    return () => {
      isMounted = false;
      abortController.abort();
      setClientId(null);
      setClientAccounts([]);
      setSelectedClientAccount(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const selectAccount = async (
    accountId: number,
    isUsePersistenceService = true,
    clientAccountsList: TClientAccount[] = clientAccounts,
    clientIdValue: number | null = clientId,
  ): Promise<void> => {
    const accountIdList = clientAccountsList.map((account) => account.id);
    const account = clientAccountsList.find((acc) => acc.id === accountId);

    if (!account) return;

    if (config.VITE_ALL_SETTINGS_LOCAL) {
      setSelectedClientAccount(account);

      return;
    }

    if (isUsePersistenceService && clientIdValue) {
      await $axios.post(`${config.VITE_URL_PERSISTENCE_SERVICE}/api/v1/last-selected-account/set`, {
        client_id_external: clientIdValue,
        account_id_external: accountId,
        account_id_external_list: accountIdList,
      });
    }

    setSelectedClientAccount(account);
  };

  const maskLogin = (login: number | null): string => {
    if (login === null) {
      return '';
    }

    if (!config.VITE_ACCOUNT_MASK) {
      return login.toString();
    }

    return '*' + login.toString().slice(-5);
  };

  const maskLoginDesc = (desc: string): string => {
    if (!config.VITE_ACCOUNT_MASK) {
      return desc;
    }

    return desc
      .replace(/\b(?:MetaTrader\s*[45]|MT\s*[45])\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  useGlobalLoading(isLoading);

  if (isLoading) {
    return <></>;
  }

  if (hasLoadError) {
    return <LoadError />;
  }

  if (!clientAccounts.length) {
    return <EmptyAccounts />;
  }

  return (
    <Account.Provider
      value={{
        selectedClientAccountId: selectedClientAccount?.id || null,
        selectedClientAccount: selectedClientAccount || null,
        clientAccounts,
        clientId,
        selectAccount,
        maskLogin,
        maskLoginDesc,
      }}
    >
      {children}
    </Account.Provider>
  );
};
